<template>
  <div>
    <v-row>
      <v-col cols="6"><h1 class="font-weight-thin">Pack Administration</h1></v-col>
    </v-row>
    <div class="elevation-0">
      <v-app-bar flat color="gray" dense>
        <v-toolbar-title class="text-h6 pl-0 font-weight-thin">
          Pack List
        </v-toolbar-title>
        <v-spacer></v-spacer>
        <v-btn @click="navigateToCreateForm()" small outlined color="primary" data-cy="createButton">
          <v-icon class="mr-2" small>fas fa-plus</v-icon>Add
        </v-btn>
      </v-app-bar>
      <v-card class="elevation-0">
        <v-card-text>
          <generic-loader caption="Fetching pack data..." topMargin="2" v-if="packDataIsLoading"></generic-loader>
          <v-data-table
            :footer-props="{
              itemsPerPageOptions: [5, 10, 25, 50],
            }"
            :headers="headerList"
            :items="packList"
            :items-per-page="25"
            :search="search"
            :sort-by.sync="sortBy"
            :sort-desc.sync="sortDesc"
            data-cy="packList"
            fixed-header
            item-key="id"
            v-else
          >
            <template v-slot:top>
              <v-row>
                <v-col sm="12" md="6">
                  <v-text-field
                    class="ml-2"
                    clearable
                    data-cy="packSearch"
                    dense
                    label="Search"
                    outlined
                    prepend-inner-icon="search"
                    single-line
                    v-model="search"
                  ></v-text-field>
                </v-col>
              </v-row>
            </template>
            <template v-slot:item="{ item }">
              <tr>
                <td data-cy="id">{{ item.id }}</td>
                <td data-cy="name">
                  <router-link :to="`/admin/pack/${item.id}`">{{ item.title }}</router-link>
                </td>
                <td data-cy="status">{{ item.status }}</td>
                <td data-cy="type">{{ item.type }}</td>
                <td data-cy="packLead">{{ item.packLeadDisplayName }}</td>
                <td class="pack-dates" data-cy="dateQaStart">{{ formatDate(item.dateQaStart) }}</td>
                <td class="pack-dates" data-cy="dateQaEnd">{{ formatDate(item.dateQaEnd) }}</td>
                <td class="pack-dates" data-cy="productionDate">{{ formatDate(item.productionDate) }}</td>
                <td data-cy="usage">
                  <div class="text-left" v-if="item.releaseNoteTotalAll">
                    <v-btn
                      @click.stop="openReleaseNoteListDialog(item)"
                      class="ma-1"
                      color="primary"
                      small
                      text
                      data-cy="releaseNoteListDialog"
                      >Release Notes: {{ item.releaseNoteTotalAll }}</v-btn
                    >
                  </div>
                </td>

                <td class="text-right" data-cy="actions">
                  <pack-delete-dialog
                    :pack="item"
                    class="ma-1"
                    data-cy="deleteButton"
                    v-if="!item.releaseNoteTotalAll"
                  ></pack-delete-dialog>
                  <v-btn class="ma-1" @click="navigateToEditForm(item.id)" color="primary" outlined x-small>Edit</v-btn>
                </td>
              </tr>
            </template>
          </v-data-table>
        </v-card-text>
      </v-card>
    </div>
    <release-note-list-dialog
      :pack="selectedPack"
      :releaseNoteList="selectedReleaseNoteList"
      v-model="showReleaseNoteListDialog"
    ></release-note-list-dialog>
  </div>
</template>

<script>
import moment from "moment";
import { mapActions, mapState } from "vuex";
import GenericLoader from "@/components/GenericLoader";
import PackDeleteDialog from "./PackDeleteDialog";
import ReleaseNoteListDialog from "./AdminPackReleaseNoteListDialog";

export default {
  components: {
    GenericLoader,
    PackDeleteDialog,
    ReleaseNoteListDialog,
  },
  computed: {
    ...mapState({
      packList: state => state.pack.packList,
      selectedReleaseNoteList: state => state.releaseNote.releaseNoteList,
    }),
  },
  async created() {
    try {
      this.packDataIsLoading = true;
      await this.fetchPackList();
    } catch (e) {
      console.error(e);
      this.$notifyError({ message: "Error fetching pack list." });
    }
    this.packDataIsLoading = false;
  },
  data() {
    return {
      headerList: [
        {
          text: "ID",
          value: "id",
        },
        {
          text: "Pack Title",
          value: "title",
        },
        {
          text: "Pack Status",
          value: "status",
        },
        {
          text: "Pack Type",
          value: "type",
        },
        {
          text: "Pack Lead",
          value: "packLead",
        },
        {
          text: "QAT Start",
          value: "dateQaStart",
        },
        {
          text: "QAT End",
          value: "dateQaEnd",
        },
        {
          text: "Production Date",
          value: "productionDate",
        },
        {
          sortable: false,
          text: "Usage",
          value: "usage",
        },
        {
          sortable: false,
          value: "actions",
          width: "165px",
        },
      ],
      packDataIsLoading: false,
      search: null,
      selectedPack: undefined,
      showReleaseNoteListDialog: false,
      sortBy: "productionDate",
      sortDesc: true,
    };
  },
  methods: {
    formatDate(dateString) {
      if (!dateString) {
        return "TBA";
      }
      return moment(dateString).format("YYYY-MM-DD");
    },
    navigateToCreateForm() {
      this.$router.push({ name: "packCreate" });
    },
    navigateToEditForm(id) {
      this.$router.push({ name: "packEdit", params: { id } });
    },
    async openReleaseNoteListDialog(pack) {
      this.selectedPack = pack;
      try {
        await this.fetchReleaseNoteList({ id: pack.id });
        this.showReleaseNoteListDialog = true;
      } catch (e) {
        console.error(e);
        this.$notifyError({ message: "Error fetching release notes for pack." });
      }
    },
    ...mapActions({
      fetchPackList: "pack/getList",
      fetchReleaseNoteList: "releaseNote/getReleaseNoteList",
    }),
  },
};
</script>

<style scoped>
.pack-dates {
  white-space: nowrap;
}
</style>
